import { useQuery } from '@tanstack/react-query';
import { getServices } from '@infrastructure/services';
import { useSession } from '../providers/SessionProvider';
import { useScopedAreaId } from '../providers/AreaFocusProvider';

/**
 * The name of the area currently in focus.
 *
 * Several headers read "Tecnología · septiembre 2026", and the session carries the
 * area *id*, not its name — the name is editable and would go stale in a claim.
 * Resolving it here keeps that one query shared across the screens that need it
 * rather than each one fetching the area list for itself.
 *
 * Falls back to a neutral label rather than blank, so a header never renders with
 * a dangling separator while the query is in flight.
 */
export const useAreaName = (fallback = 'Tu área'): string => {
  const session = useSession();
  /* Sigue al foco: si un administrador global está mirando Comercial, la cabecera
     tiene que decir «Comercial» — y no el área a la que él pertenece, que no es
     lo que hay debajo. */
  const areaId = useScopedAreaId(session.areaId);
  return useNameOf(areaId, fallback);
};

/**
 * El nombre del área **de quien mira**, mire lo que mire.
 *
 * Hacen falta las dos y la diferencia no es cosmética: la cabecera describe lo
 * que hay debajo —y por eso sigue al foco— mientras que el rótulo bajo el nombre
 * de alguien dice quién es. Con una sola, un administrador global mirando
 * Académico aparecía como si fuera de Académico. Se vio en pantalla, no leyendo
 * el código.
 */
export const useOwnAreaName = (fallback = 'Tu área'): string => {
  const session = useSession();
  return useNameOf(session.areaId, fallback);
};

/* Una consulta compartida por las dos: mismo `queryKey`, una sola petición. */
const useNameOf = (areaId: string, fallback: string): string => {
  const areas = useQuery({
    queryKey: ['organization', 'areas'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  return (areas.data ?? []).find((area) => area.id === areaId)?.name ?? fallback;
};
